import React, { useState, useMemo } from 'react';
import { Gavel, Check, X, Minus, ShieldCheck, AlertCircle, Plus, Edit2, Trash2, Save } from 'lucide-react';
import { Card, Badge, Button, Input } from '../ui';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { cn } from '../../lib/utils';

interface Chapter {
  id: string;
  name: string;
  delegates: number; // The voting weight
  vote: 'yes' | 'no' | 'abstain' | null;
}

const INITIAL_CHAPTERS: Chapter[] = [
  { id: '1', name: 'Local 104 (San Francisco)', delegates: 450, vote: null },
  { id: '2', name: 'Local 39 (Sacramento)', delegates: 320, vote: null },
  { id: '3', name: 'Local 11 (Los Angeles)', delegates: 850, vote: null },
  { id: '4', name: 'Local 78 (San Diego)', delegates: 410, vote: null },
  { id: '5', name: 'Local 46 (Seattle)', delegates: 520, vote: null },
  { id: '6', name: 'Local 290 (Portland)', delegates: 380, vote: null },
  { id: '7', name: 'Local 3 (Denver)', delegates: 290, vote: null },
];

const COLORS = {
  yes: '#10b981', // emerald
  no: '#ef4444', // red
  abstain: '#94a3b8', // slate
  uncast: '#e2e8f0' // slate-200
};

export function MultiChapterBallotValidator() {
  const [chapters, setChapters] = useState<Chapter[]>(INITIAL_CHAPTERS);
  const [editModeId, setEditModeId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDelegates, setEditDelegates] = useState('');

  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDelegates, setNewDelegates] = useState('');

  const handleVote = (id: string, vote: 'yes' | 'no' | 'abstain') => {
    setChapters(prev => prev.map(c => c.id === id ? { ...c, vote } : c));
  };

  const handleAdd = () => {
    if (!newName.trim() || !newDelegates) return;
    const delegates = parseInt(newDelegates) || 0;
    const newChapter: Chapter = {
      id: Math.random().toString(36).substr(2, 9),
      name: newName,
      delegates,
      vote: null
    };
    setChapters([newChapter, ...chapters]);
    setNewName('');
    setNewDelegates('');
    setIsAdding(false);
  };

  const startEdit = (chapter: Chapter) => {
    setEditModeId(chapter.id);
    setEditName(chapter.name);
    setEditDelegates(chapter.delegates.toString());
  };

  const saveEdit = (id: string) => {
    const delegates = parseInt(editDelegates) || 0;
    setChapters(chapters.map(c => c.id === id ? { ...c, name: editName, delegates } : c));
    setEditModeId(null);
  };

  const removeChapter = (id: string) => {
    setChapters(chapters.filter(c => c.id !== id));
  };

  const model = useMemo(() => {
    let totalDelegates = 0;
    let yesVotes = 0;
    let noVotes = 0;
    let abstainVotes = 0;
    let uncastVotes = 0;

    chapters.forEach(c => {
      totalDelegates += c.delegates;
      if (c.vote === 'yes') yesVotes += c.delegates;
      else if (c.vote === 'no') noVotes += c.delegates;
      else if (c.vote === 'abstain') abstainVotes += c.delegates;
      else uncastVotes += c.delegates;
    });

    const totalCast = yesVotes + noVotes + abstainVotes;
    const turnout = totalDelegates > 0 ? (totalCast / totalDelegates) * 100 : 0;
    
    // For pass/fail, usually abstains don't count towards the denominator in Roberts Rules, 
    // but we'll calculate raw percentage of votes cast.
    const activeVotes = yesVotes + noVotes;
    const yesPercentage = activeVotes > 0 ? (yesVotes / activeVotes) * 100 : 0;
    
    const passesSimple = yesPercentage > 50;
    const passesSuper = yesPercentage >= 66.67;

    const chartData = [
      { name: 'Yes', value: yesVotes, color: COLORS.yes },
      { name: 'No', value: noVotes, color: COLORS.no },
      { name: 'Abstain', value: abstainVotes, color: COLORS.abstain },
      { name: 'Uncast', value: uncastVotes, color: COLORS.uncast }
    ].filter(d => d.value > 0);

    return {
      totalDelegates,
      totalCast,
      turnout,
      yesVotes,
      noVotes,
      abstainVotes,
      uncastVotes,
      yesPercentage,
      passesSimple,
      passesSuper,
      chartData
    };
  }, [chapters]);

  return (
    <div className="max-w-6xl mx-auto space-y-8 p-4">
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-black flex items-center mb-2">
            <Gavel className="w-6 h-6 mr-3 text-slate-300" />
            Weighted Ballot Validator
          </h2>
          <p className="text-slate-300 max-w-xl">
            Instantly validate convention resolutions. Chapter votes are automatically weighted by their respective delegate counts to compute simple and supermajority outcomes.
          </p>
        </div>
        <div className="bg-slate-800/80 p-4 rounded-xl border border-slate-700 text-center shrink-0 min-w-[200px]">
          <div className="text-sm text-slate-400 uppercase tracking-wider font-bold mb-1">Total Active Weights</div>
          <div className="text-4xl font-black text-white">{model.totalDelegates.toLocaleString()}</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        
        {/* Voting Interface */}
        <Card className="lg:col-span-2 p-0 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 overflow-hidden">
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
            <div className="flex items-center gap-3">
               <h3 className="font-bold">Roll Call Entry</h3>
               <Badge variant="gray">{chapters.length} Locals</Badge>
            </div>
            <Button size="sm" variant="secondary" onClick={() => setIsAdding(!isAdding)}>
              <Plus className="w-4 h-4 mr-1" /> Add Local
            </Button>
          </div>
          
          {isAdding && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900 flex flex-col sm:flex-row gap-3 items-end">
               <div className="flex-1 w-full">
                 <label className="text-xs font-bold text-slate-500 mb-1 block">Local Name</label>
                 <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Local 42 (Miami)" />
               </div>
               <div className="w-full sm:w-32">
                 <label className="text-xs font-bold text-slate-500 mb-1 block">Delegates</label>
                 <Input type="number" value={newDelegates} onChange={e => setNewDelegates(e.target.value)} placeholder="0" />
               </div>
               <Button onClick={handleAdd} className="w-full sm:w-auto">Add</Button>
               <Button variant="ghost" onClick={() => setIsAdding(false)}>Cancel</Button>
            </div>
          )}

          <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-[600px] overflow-y-auto">
            {chapters.map((chapter) => (
              <div key={chapter.id} className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors group">
                
                {editModeId === chapter.id ? (
                  <div className="flex-1 flex flex-col sm:flex-row gap-3">
                     <Input className="flex-1" value={editName} onChange={e => setEditName(e.target.value)} />
                     <Input type="number" className="w-32" value={editDelegates} onChange={e => setEditDelegates(e.target.value)} />
                     <Button size="sm" onClick={() => saveEdit(chapter.id)}><Save className="w-4 h-4" /></Button>
                     <Button size="sm" variant="ghost" onClick={() => setEditModeId(null)}><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 flex items-center gap-3">
                      <div>
                        <div className="font-bold text-slate-900 dark:text-white">{chapter.name}</div>
                        <div className="text-sm text-slate-500 font-medium">Weight: {chapter.delegates.toLocaleString()} delegates</div>
                      </div>
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                         <button onClick={() => startEdit(chapter)} className="p-1.5 text-slate-400 hover:text-blue-500 rounded"><Edit2 className="w-3.5 h-3.5" /></button>
                         <button onClick={() => removeChapter(chapter.id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    
                    <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-lg shrink-0 mt-2 sm:mt-0">
                      <button 
                        onClick={() => handleVote(chapter.id, 'yes')}
                        className={cn(
                          "flex items-center px-4 py-2 rounded-md text-sm font-bold transition-all",
                          chapter.vote === 'yes' ? "bg-emerald-500 text-white shadow-sm" : "text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                        )}
                      >
                        <Check className="w-4 h-4 mr-1" /> Yea
                      </button>
                      <button 
                        onClick={() => handleVote(chapter.id, 'no')}
                        className={cn(
                          "flex items-center px-4 py-2 rounded-md text-sm font-bold transition-all",
                          chapter.vote === 'no' ? "bg-red-500 text-white shadow-sm" : "text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        )}
                      >
                        <X className="w-4 h-4 mr-1" /> Nay
                      </button>
                      <button 
                        onClick={() => handleVote(chapter.id, 'abstain')}
                        className={cn(
                          "flex items-center px-4 py-2 rounded-md text-sm font-bold transition-all",
                          chapter.vote === 'abstain' ? "bg-slate-500 text-white shadow-sm" : "text-slate-500 hover:text-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"
                        )}
                      >
                        <Minus className="w-4 h-4 mr-1" /> Abs
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
            {chapters.length === 0 && (
              <div className="p-8 text-center text-slate-500">
                 No locals added yet. Add one above to start voting.
              </div>
            )}
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-slate-900 text-white">
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4">Resolution Status</h3>
            
            <div className="space-y-4">
              <div className={cn("p-4 rounded-xl border flex items-center justify-between", model.passesSimple ? "bg-emerald-900/40 border-emerald-500/30" : "bg-slate-800 border-slate-700")}>
                <div>
                  <div className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Simple Majority (&gt;50%)</div>
                  <div className={cn("text-xl font-black", model.passesSimple ? "text-emerald-400" : "text-slate-300")}>
                    {model.passesSimple ? 'PASSED' : 'FAILED'}
                  </div>
                </div>
                {model.passesSimple ? <ShieldCheck className="w-8 h-8 text-emerald-500" /> : <AlertCircle className="w-8 h-8 text-slate-500" />}
              </div>

              <div className={cn("p-4 rounded-xl border flex items-center justify-between", model.passesSuper ? "bg-emerald-900/40 border-emerald-500/30" : "bg-slate-800 border-slate-700")}>
                <div>
                  <div className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Supermajority (&ge;66.6%)</div>
                  <div className={cn("text-xl font-black", model.passesSuper ? "text-emerald-400" : "text-slate-300")}>
                    {model.passesSuper ? 'PASSED' : 'FAILED'}
                  </div>
                </div>
                {model.passesSuper ? <ShieldCheck className="w-8 h-8 text-emerald-500" /> : <AlertCircle className="w-8 h-8 text-slate-500" />}
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-800">
              <div className="text-sm text-slate-400 uppercase tracking-wider font-bold mb-1">Weighted Approval</div>
              <div className="text-4xl font-black text-white">{model.yesPercentage.toFixed(1)}%</div>
              <p className="text-xs text-slate-500 mt-2">Calculated from Yeas and Nays. Abstains do not impact the denominator.</p>
            </div>
          </Card>

          <Card className="p-6 border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4">Vote Distribution</h3>
            <div className="h-[200px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={model.chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {model.chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value: any) => Number(value).toLocaleString()}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend verticalAlign="bottom" height={36}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

      </div>
    </div>
  );
}
